import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Rocket, Lock, Layers, ArrowDown } from "lucide-react";
import { getPairs, getStats } from "@/lib/api";
import { formatUsd } from "@/lib/format";
import LaunchModal from "@/components/LaunchModal";
import { useCountUp } from "@/hooks/useAnimations";

export default function Hero() {
  const [open, setOpen] = useState(false);
  const { data: pairs = [] } = useQuery({ queryKey: ["pairs"], queryFn: getPairs });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: getStats });

  return (
    <section className="bg-grid relative overflow-hidden border-b border-white/5">
      <div className="bg-dots absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="max-w-3xl">
          <span className="animate-rise delay-100 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1.5 text-xs font-semibold text-[#7fb0ff]">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00f0ff] opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00f0ff]" />
            </span>
            Base Chain · Powered by Uniswap v4
          </span>
          <h1 className="animate-rise delay-200 mt-5 font-head text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
            Launch a coin <span className="text-gradient">paired with</span>
            <br />
            anything on Base.
          </h1>
          <p className="animate-rise delay-300 mt-5 max-w-xl text-base text-slate-400 sm:text-lg">
            One transaction opens your coin's own Uniswap v4 market — priced in ETH, cbBTC,
            or tokenized stocks like NVDA & TSLA. Liquidity locked forever. A 2% swap tax pays you 60%, in the paired token.
          </p>
          <div className="animate-rise-bounce delay-400 mt-8 flex flex-wrap items-center gap-3">
            <button
              data-testid="hero-launch-button"
              onClick={() => setOpen(true)}
              className="btn-accent flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold text-white active:scale-[0.98]"
            >
              <Rocket size={18} /> Launch a coin
            </button>
            <a
              href="#coins"
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#131824] px-6 py-3.5 text-sm font-semibold text-slate-100 transition-all duration-200 hover:border-white/25 hover:bg-white/5 active:scale-[0.98]"
            >
              Explore coins <ArrowDown size={16} />
            </a>
          </div>

          <div className="animate-rise delay-500 mt-9 flex flex-wrap gap-6">
            <Stat label="Total market cap" target={stats?.total_market_cap_usd ?? 0} money icon={<Layers size={14} />} />
            <Stat label="24h volume" target={stats?.total_volume_24h_usd ?? 0} money icon={<Rocket size={14} />} />
            <Stat label="Coins launched" target={stats?.total_coins ?? 0} icon={<Lock size={14} />} />
          </div>
        </div>
      </div>

      {/* pair marquee */}
      <div className="group animate-fade-in delay-650 relative border-t border-white/5 py-4">
        <div className="flex w-max animate-marquee gap-3 px-4">
          {[...pairs, ...pairs].map((p, i) => (
            <div key={i} className="flex items-center gap-2 rounded-full border border-white/10 bg-[#131824]/80 px-3 py-1.5">
              <img src={p.logo} alt={p.symbol} className="h-5 w-5 rounded-full" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              <span className="font-mono2 text-xs font-semibold text-slate-300">{p.symbol}</span>
              <span className="text-[10px] uppercase tracking-wide text-slate-600">{p.category}</span>
            </div>
          ))}
        </div>
      </div>

      <LaunchModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
}

function Stat({ label, target, icon, money }) {
  const v = useCountUp(target);
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">{icon}{label}</div>
      <div className="font-mono2 text-2xl font-bold text-slate-50">{money ? formatUsd(v) : Math.round(v)}</div>
    </div>
  );
}
